/**
 * `useChatViewStore` — the Chat surface's view state, lifted out of the route component so it
 * survives navigating away and back, and persisted to `localStorage` so it also survives a
 * reload within the same browser session (frontend.md §2.2).
 *
 * `<ChatSurface>` and `<ChatComposer>` unmount when the user switches to another surface
 * (Editor / Search / …). Holding the open-session id, the rail-collapsed flag, and the unsent
 * composer draft (+ workflow/mode selection) in this store means returning to /chat restores
 * exactly where the user left off — including re-opening the in-flight/just-completed session
 * (the message list refetches from the persisted `activeSessionId`).
 *
 * Transient turn state (the streaming `StreamingTurn`, the pending-query preview) is NOT here —
 * an in-flight turn is abandoned on unmount by design; on return the session refetch shows the
 * persisted result.
 */

import { create } from 'zustand';

/** The persisted slice (localStorage-backed). */
interface ChatViewPersisted {
  /** The open session id; null = a fresh, not-yet-created session. */
  activeSessionId: string | null;
  /** Whether the recent-sessions rail is collapsed (default collapsed). */
  railCollapsed: boolean;
  /** The unsent composer text. */
  draft: string;
  /** The composer's workflow-routing selection ('' = auto). */
  workflow: string;
  /** The composer's tool-surface tier. */
  mode: 'fast' | 'deep';
}

interface ChatViewState extends ChatViewPersisted {
  setActiveSessionId: (id: string | null) => void;
  setRailCollapsed: (collapsed: boolean) => void;
  setDraft: (draft: string) => void;
  setWorkflow: (workflow: string) => void;
  setMode: (mode: 'fast' | 'deep') => void;
}

const STORAGE_KEY = 'aletheia.chatView';

const DEFAULTS: ChatViewPersisted = {
  activeSessionId: null,
  railCollapsed: true,
  draft: '',
  workflow: '',
  mode: 'fast',
};

/** Read the persisted slice from localStorage; defaults on miss / parse error. */
function loadPersisted(): ChatViewPersisted {
  try {
    if (typeof localStorage === 'undefined') return DEFAULTS;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<ChatViewPersisted>;
    return {
      activeSessionId:
        typeof parsed.activeSessionId === 'string' ? parsed.activeSessionId : null,
      railCollapsed:
        typeof parsed.railCollapsed === 'boolean' ? parsed.railCollapsed : DEFAULTS.railCollapsed,
      draft: typeof parsed.draft === 'string' ? parsed.draft : '',
      workflow: typeof parsed.workflow === 'string' ? parsed.workflow : '',
      mode: parsed.mode === 'deep' ? 'deep' : 'fast',
    };
  } catch {
    return DEFAULTS;
  }
}

export const useChatViewStore = create<ChatViewState>((set) => ({
  ...loadPersisted(),
  setActiveSessionId: (activeSessionId) => set({ activeSessionId }),
  setRailCollapsed: (railCollapsed) => set({ railCollapsed }),
  setDraft: (draft) => set({ draft }),
  setWorkflow: (workflow) => set({ workflow }),
  setMode: (mode) => set({ mode }),
}));

// Write-through: persist the slice to localStorage on every change (quota / unavailable is
// non-fatal — the worst case is the view doesn't survive a reload).
useChatViewStore.subscribe((s) => {
  try {
    if (typeof localStorage === 'undefined') return;
    const slice: ChatViewPersisted = {
      activeSessionId: s.activeSessionId,
      railCollapsed: s.railCollapsed,
      draft: s.draft,
      workflow: s.workflow,
      mode: s.mode,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(slice));
  } catch {
    /* non-fatal */
  }
});
